import { PropTypes } from 'prop-types';
import { TourProvider } from '@reactour/tour';
import { useOnboardingTour } from '../hooks/useOnboardingTour';

export const SidebarTourConfig = [
  {
    selector: '#sidebar-menu',
    content:
      'Welcome to the Voxel Readers! Here you can navigate through different sections of the application.',
  },
  {
    selector: '#dashboard',
    content:
      'This is your dashboard section where you can view overall statistics and reports.',
  },
  {
    selector: '.menu-side',
    content: 'Each menu item has an icon to help you identify its purpose.',
  },
  {
    selector: '.submenu',
    content: 'Click on menu items to expand them and see additional options.',
  },
  {
    selector: '.menu-arrow',
    content: 'Items with arrows contain sub-menus with more specific features.',
  },
  {
    selector: '#cases',
    content: 'Manage all case-related information and records here.',
  },
  {
    selector: '#users',
    content: 'Manage all users-related information and records here.'
  },
  {
    selector: '#branches',
    content: 'Manage all branch-related information and records here.'
  },
  {
    selector: '#revenue',
    content: 'Manage all accounts-related information and records here.',
  },
  {
    selector: '#account-profile',
    content: 'Manage your profile here.',
  },
];

export const createOnboardingTour = (navigate) => [
  {
    selector: '#account-profile',
    content: 'Welcome to Voxel Reader! Let\'s take a quick tour. First, click on your account profile dropdown to see available options.',
    position: 'bottom-start',
    stepInteraction: false,
    action: () => {
      setTimeout(() => {
        const dropdown = document.querySelector('#account-profile .dropdown-toggle');
        if (dropdown && !document.querySelector('.dropdown-menu.show')) {
          dropdown.click();
        }
      }, 500);
    },
  },
  {
    selector: '#settings-link',
    content: 'Great! Here you can find the Settings option. This is where you can manage your account preferences and payment methods.',
    position: 'right',
    highlightedSelectors: ['#settings-link'],
    stepInteraction: false,
    action: () => {
      // Keep dropdown open during this step
      setTimeout(() => {
        const dropdownOptions = document.querySelector('#account-profile .dropdown-menu');
        if (dropdownOptions) {
          dropdownOptions.classList.add('show');
          dropdownOptions.style.display = 'block';
          dropdownOptions.style.position = 'absolute';
          dropdownOptions.style.inset = '0px auto auto 0px';
          dropdownOptions.style.margin = '0px';
          dropdownOptions.style.transform = 'translate3d(0px, 72px, 0px)';
        }
        // Navigate to settings
        navigate('/user/settings');
      }, 300);
    },
  },
  {
    selector: '#edit-payment-btn',
    content: 'Perfect! Now you\'re on the Settings page. Here you can click the "Edit" button to update your payment method and manage your billing preferences. This completes your tour!',
    position: 'top',
    stepInteraction: false,
    action: async () => {

      // Hide dropdown and wait for button to be ready
      setTimeout(() => {
        const dropdown = document.querySelector('#account-profile .dropdown-menu');
        if (dropdown) {
          dropdown.classList.remove('show');
        }
      }, 100);

      return new Promise((resolve) => {
        const maxWaitTime = 5000; // max 5 seconds
        const intervalTime = 100; // poll every 100ms
        let waited = 0;

        const checkElement = () => {
          const el = document.querySelector('#edit-payment-btn');
          if (el) {
            resolve();
          } else if (waited >= maxWaitTime) {
            console.warn('Tour: Element not found after 5s');
            resolve(); // fail gracefully
          } else {
            waited += intervalTime;
            setTimeout(checkElement, intervalTime);
          }
        };

        checkElement();
      });
    },
  }
];

export const TourWrapper = ({ children }) => {
  return (
    <TourProvider
      steps={SidebarTourConfig}
      showNavigation={true}
      showBadge={true}
      showCloseButton={true}
      styles={{
        popover: (base) => ({
          ...base,
          '--reactour-accent': '#00d3c7',
          borderRadius: 10,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          maxWidth: '350px'
        }),
        badge: (base) => ({
          ...base,
          background: '#00d3c7',
          color: 'white'
        }),
        controls: (base) => ({
          ...base,
          marginTop: 20,
        }),
        dot: (base, { current }) => ({
          ...base,
          backgroundColor: current ? '#00d3c7' : '#ccc'
        })
      }}
      onClickHighlighted={(e) => {
        e?.preventDefault();
        e?.stopPropagation();
      }}
      onClickMask={({ setIsOpen }) => setIsOpen(false)}
      nextButton={(props) => <TourNextButton {...props} />}
    >
      {children}
    </TourProvider>
  );
};
TourWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

const TourNextButton = ({
  Button,
  currentStep,
  stepsLength,
  setCurrentStep,
  steps,
}) => {
  const { completeTour } = useOnboardingTour();
  const last = currentStep === stepsLength - 1;

  return (
    <Button
      hideArrow={last}
      onClick={() => {
        if (last) {
          completeTour();
        } else {
          setCurrentStep((s) => (s === steps?.length - 1 ? 0 : s + 1));
        }
      }}
    >
      {last ? 'Done' : null}
    </Button>
  );
};

TourNextButton.propTypes = {
  Button: PropTypes.elementType.isRequired,
  currentStep: PropTypes.number.isRequired,
  stepsLength: PropTypes.number.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  steps: PropTypes.array.isRequired,
};
